import {useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import ICON_MORE_LIGHT from '../../../../public/static/v4/common/icon-more-light.svg';
import ICON_MORE from '../../../../public/static/v4/common/icon-more.svg';
import {CommentTrackingContext} from '../../../comment/common';
import CommentContent from '../../../comment/component/CommentContent';
import {ICommentContent} from '../../../comment/type';
import Anchor from '../../../common/component/Anchor';
import Text from '../../../common/component/Text';
import useTheme from '../../../common/hook/useTheme';
import {formatNumber} from '../../../common/util/number';
import {sendViewerEvent} from '../../common';
import {IViewType} from '../../type';

interface IProps {
  contentId: number;
  episodeId: number;
  comments: ICommentContent[];
  totalCount: number;
  showMoreButton: boolean;
  viewType: IViewType;
  openCommentModal: () => {};
}

export default function Comments({
  contentId,
  episodeId,
  comments,
  totalCount,
  viewType,
  openCommentModal,
}: IProps) {
  const {t} = useTranslation();
  const {theme} = useTheme();

  const onClick = (e: EventObject) => {
    e.stopPropagation();
    openCommentModal();
    sendViewerEvent({
      actionName: 'comment_more',
      action: 'click',
      customProps: {event_layer: 'comment', viewer_type: viewType},
    });
  };

  const contextValue = useMemo(
    () => ({
      section: 'viewer',
      page: 'viewer',
      pageContentId: contentId,
      pageEpisodeId: episodeId,
    }),
    [contentId, episodeId]
  );

  return (
    <>
      <Anchor
        className={cn('!flex items-center py-18', {
          'border-b border-solid border-grey-03':
            comments.length > 0 || totalCount === 0,
        })}
        onClick={onClick}
      >
        <Text className="grow s16-bold-white-black">
          {totalCount > 0
            ? t('viewer_end_comment_count_v4', {
                value: formatNumber(totalCount),
              })
            : t('viewer_end_comment_count_zero')}
        </Text>
        <img
          src={theme === 'light' ? ICON_MORE_LIGHT : ICON_MORE}
          className="w-24 h-24"
          alt=""
        />
      </Anchor>
      {comments.length > 0 && (
        <CommentTrackingContext.Provider value={contextValue}>
          <div className="overflow-hidden">
            {comments.map((comment, index) => (
              <div
                key={comment.id}
                className={cn('mb-30', {'mt-18': index === 0})}
                onClick={onClick}
              >
                <CommentContent
                  comment={comment}
                  contentMaxLine={3}
                  viewType={viewType}
                />
              </div>
            ))}
          </div>
        </CommentTrackingContext.Provider>
      )}
      {totalCount === 0 && (
        <Text className="s14-regular-grey08 text-center py-28">
          {t('comment_empty_first')}
        </Text>
      )}
    </>
  );
}
